import React from 'react';
import { interpolate, Easing } from 'remotion';
import { SpeakerLabelTransitionEffect } from './types';

export interface TransitionSpeakerLabelInParams {
  fps: number;
  frame: number;
  animationType: SpeakerLabelTransitionEffect;
  showAfter?: number;
  hideAfter?: number;
  duration?: number;
  showAgainOnNewSpeaker?: boolean;
}

/**
 * Generates CSS styles for speaker label entrance animations based on speaker-relative timing.
 *
 * Animation Timeline (Speaker-Relative):
 * - frame: Frames since THIS SPEAKER started speaking (not absolute video time)
 * - BEFORE showAfter: completely hidden (opacity: 0)
 * - DURING entrance animation (showFrame → showFrame + animationDuration): animated entrance
 * - AFTER entrance: fully visible (returns empty object to preserve base styles)
 * - WITH showAgainOnNewSpeaker: Re-triggers entrance animation when speaker changes
 *
 * @param params Animation parameters
 * @param params.fps Frames per second
 * @param params.frame Speaker-relative frame (frames since speaker started speaking)
 * @param params.animationType Type of animation effect
 * @param params.showAfter Time in seconds after speaker starts to show label (default: 0)
 * @param params.hideAfter Duration in seconds to stay fully visible before hiding (optional)
 * @param params.duration Animation duration in seconds (default: 0.3)
 * @param params.showAgainOnNewSpeaker Re-trigger animation on speaker change (default: false)
 * @returns CSS properties for the current frame
 */
export function getTransitionSpeakerLabelInStyles({
  fps,
  frame,
  animationType,
  showAfter = 0,
  hideAfter,
  duration = 0.3
}: TransitionSpeakerLabelInParams): React.CSSProperties {
  // Calculate visibility window based on speaker-relative timeline
  const showFrame = Math.floor(showAfter * fps);
  const animationDuration = Math.ceil(duration * fps);
  // hideAfter is duration to stay visible AFTER becoming fully visible
  const labelVisibleFrame = showFrame + animationDuration;
  const hideFrame = hideAfter !== undefined ? labelVisibleFrame + Math.floor(hideAfter * fps) : Infinity;

  // BEFORE showAfter: completely hidden
  if (frame < showFrame) {
    return { opacity: 0, pointerEvents: 'none' };
  }

  // Handle None animation type: instant show without animation
  if (animationType === SpeakerLabelTransitionEffect.None) {
    // Already visible after showAfter
    return {};
  }

  // DURING entrance animation (showFrame → showFrame + animationDuration)
  if (frame < showFrame + animationDuration) {
    const progress = interpolate(frame, [showFrame, showFrame + animationDuration], [0, 1], {
      easing: Easing.out(Easing.ease),
      extrapolateRight: 'clamp'
    });

    switch (animationType) {
      case SpeakerLabelTransitionEffect.Fade:
        return { opacity: progress };

      case SpeakerLabelTransitionEffect.MoveLeft: {
        const translateX = interpolate(progress, [0, 1], [-110, 0]);
        return { transform: `translateX(${translateX}%)`, opacity: progress };
      }

      case SpeakerLabelTransitionEffect.MoveBottom: {
        const translateY = interpolate(progress, [0, 1], [110, 0]);
        return { transform: `translateY(${translateY}%)`, opacity: progress };
      }

      case SpeakerLabelTransitionEffect.Scale: {
        const scale = interpolate(progress, [0, 1], [0.5, 1]);
        return { transform: `scale(${scale})`, opacity: progress };
      }

      case SpeakerLabelTransitionEffect.Mask: {
        const clipPath = `inset(0 ${100 - progress * 100}% 0 0)`;
        return { clipPath, opacity: 1 };
      }

      default:
        return {};
    }
  }

  // AFTER hideAfter: let exit animation take control
  // Return empty object to allow transitionSpeakerLabelOut to handle the exit animation
  if (hideAfter !== undefined && frame >= hideFrame) {
    return {};
  }

  // FULLY VISIBLE: between end of entrance and start of exit
  return {};
}
